import { useEffect, useMemo, useState } from "react";
import type { FormDefinition, SubmissionAnswer } from "@responsescope/shared";
import { FILE_LIMITS, formatMimeList, formatMaxSize } from "@responsescope/shared";
import { fetchForm, fetchGeo, submitForm } from "./lib/api.js";
import { getInitData, getTelegramWebApp } from "./lib/telegram.js";
import { FieldRenderer } from "./components/FieldRenderer.js";

type Stage = "loading" | "error" | "form" | "confirm" | "submitting" | "done";

// Runtime ?form=<slug> (set by the bot's per-campaign deep links, see
// apps/api/src/telegram/updateHandler.ts#FORM_SLUG_BY_START_PARAM) takes
// priority over the build-time default, so one deployment of this Mini App
// can serve multiple forms without a rebuild.
const FORM_SLUG = new URLSearchParams(window.location.search).get("form") || import.meta.env.VITE_FORM_SLUG || "general-intake";

// Hardcoded to the one live form's field_key, same pragmatic choice already
// made in apps/admin for category/region - see ARCHITECTURE.md.
const REGION_FIELD_KEY = "region";

function attachmentHint(fieldType: "image" | "file", maxFiles: number | undefined): string {
  const limits = fieldType === "image" ? FILE_LIMITS.IMAGE : FILE_LIMITS.DOCUMENT;
  const count = maxFiles ?? 5;
  return `対応形式: ${formatMimeList(limits.ALLOWED_MIME_TYPES)} / 最大 ${formatMaxSize(limits.MAX_SIZE_BYTES)}・${count}件まで`;
}

function isFieldVisible(field: FormDefinition["fields"][number], answers: Record<string, string>): boolean {
  if (!field.condition) return true;
  return answers[field.condition.fieldKey] === field.condition.equals;
}

export function App() {
  const [stage, setStage] = useState<Stage>("loading");
  const [form, setForm] = useState<FormDefinition | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [files, setFiles] = useState<Record<string, File[]>>({});
  const [errorMessage, setErrorMessage] = useState("");
  const [receiptNo, setReceiptNo] = useState("");
  const [regionAutoDetected, setRegionAutoDetected] = useState(false);

  useEffect(() => {
    getTelegramWebApp()?.ready();
    getTelegramWebApp()?.expand();

    fetchForm(FORM_SLUG)
      .then((f) => {
        setForm(f);
        setStage("form");

        // Auto-detect is a pre-fill suggestion only - the field stays a
        // normal, fully editable select either way (see
        // ARCHITECTURE.md#region-auto-detection).
        const regionField = f.fields.find((field) => field.fieldKey === REGION_FIELD_KEY);
        if (!regionField) return;
        fetchGeo().then((geo) => {
          if (!geo.prefecture) return;
          if (!regionField.options.some((o) => o.value === geo.prefecture)) return;
          setAnswers((prev) => (prev[REGION_FIELD_KEY] ? prev : { ...prev, [REGION_FIELD_KEY]: geo.prefecture! }));
          setRegionAutoDetected(true);
        });
      })
      .catch((err: unknown) => {
        setErrorMessage(err instanceof Error ? err.message : "unknown error");
        setStage("error");
      });
  }, []);

  const visibleFields = useMemo(() => (form ? form.fields.filter((f) => isFieldVisible(f, answers)) : []), [form, answers]);

  const missingRequired = useMemo(
    () =>
      visibleFields.filter((f) => {
        if (f.fieldType === "image" || f.fieldType === "file") return f.required && (files[f.fieldKey]?.length ?? 0) === 0;
        return f.required && !answers[f.fieldKey]?.trim();
      }),
    [visibleFields, answers, files]
  );

  async function handleSubmit() {
    if (!form) return;
    setStage("submitting");
    setErrorMessage("");

    const submissionAnswers: SubmissionAnswer[] = visibleFields
      .filter((f) => f.fieldType !== "image" && f.fieldType !== "file")
      .map((f) => ({ fieldKey: f.fieldKey, fieldType: f.fieldType, value: answers[f.fieldKey] ?? "" }));

    const fileEntries = visibleFields
      .filter((f) => f.fieldType === "image" || f.fieldType === "file")
      .flatMap((f) => (files[f.fieldKey] ?? []).map((file) => ({ fieldKey: f.fieldKey, file })));

    try {
      const result = await submitForm(form.id, submissionAnswers, fileEntries, getInitData());
      setReceiptNo(result.receiptNo);
      setStage("done");
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error");
      setStage("confirm");
    }
  }

  if (stage === "loading") return <div className="rs-center">読み込み中...</div>;
  if (stage === "error") return <div className="rs-center rs-error">エラー: {errorMessage}</div>;
  if (!form) return null;

  if (stage === "done") {
    return (
      <div className="rs-center">
        <h2>受付しました</h2>
        <p className="rs-receipt">受付番号: {receiptNo}</p>
      </div>
    );
  }

  if (stage === "confirm" || stage === "submitting") {
    return (
      <div className="rs-page">
        <h1>{form.title}</h1>
        <h2>入力内容の確認</h2>
        {errorMessage && <p className="rs-error">送信に失敗しました: {errorMessage}</p>}
        <dl className="rs-confirm-list">
          {visibleFields.map((f) => (
            <div key={f.fieldKey} className="rs-confirm-row">
              <dt>{f.label}</dt>
              <dd>
                {f.fieldType === "image" || f.fieldType === "file"
                  ? (files[f.fieldKey] ?? []).map((file) => file.name).join(", ") || "(なし)"
                  : answers[f.fieldKey] || "(未入力)"}
              </dd>
            </div>
          ))}
        </dl>
        <div className="rs-actions">
          <button className="rs-button rs-button-secondary" onClick={() => setStage("form")} disabled={stage === "submitting"}>
            修正する
          </button>
          <button className="rs-button rs-button-primary" onClick={handleSubmit} disabled={stage === "submitting"}>
            {stage === "submitting" ? "送信中..." : "送信する"}
          </button>
        </div>
      </div>
    );
  }

  const sections = groupBySection(visibleFields);

  return (
    <div className="rs-page">
      <h1>{form.title}</h1>
      {form.description && <p className="rs-description">{form.description}</p>}
      {sections.map(([sectionLabel, fields]) => (
        <section key={sectionLabel} className="rs-section">
          <h2>{sectionLabel}</h2>
          {fields.map((field) => (
            <div key={field.fieldKey} className="rs-field">
              <label className="rs-label">
                {field.label}
                {field.required && <span className="rs-required">*</span>}
                {field.fieldKey === REGION_FIELD_KEY && regionAutoDetected && (
                  <span className="rs-auto-badge">自動取得（違う場合は変更してください）</span>
                )}
              </label>
              <FieldRenderer
                field={field}
                value={answers[field.fieldKey] ?? ""}
                files={files[field.fieldKey] ?? []}
                onChange={(value) => {
                  if (field.fieldKey === REGION_FIELD_KEY) setRegionAutoDetected(false);
                  setAnswers((prev) => ({ ...prev, [field.fieldKey]: value }));
                }}
                onFilesChange={(newFiles) => setFiles((prev) => ({ ...prev, [field.fieldKey]: newFiles }))}
              />
              {(field.fieldType === "image" || field.fieldType === "file") && (
                <p className="rs-hint">{attachmentHint(field.fieldType, field.maxFiles)}</p>
              )}
            </div>
          ))}
        </section>
      ))}
      <div className="rs-actions">
        <button className="rs-button rs-button-primary" onClick={() => setStage("confirm")} disabled={missingRequired.length > 0}>
          確認画面へ
        </button>
      </div>
    </div>
  );
}

function groupBySection(fields: FormDefinition["fields"]): [string, FormDefinition["fields"]][] {
  const map = new Map<string, FormDefinition["fields"]>();
  for (const field of fields) {
    const list = map.get(field.sectionLabel) ?? [];
    list.push(field);
    map.set(field.sectionLabel, list);
  }
  return Array.from(map.entries());
}
